import { useCallback, useEffect, useMemo } from 'react';
import {
  HubsDataDictionary,
  ResourceReservationsToDayMapsType,
  ResourceShiftTotalsMap,
  ResourceViolationItem,
  SchedulerDayColumn,
  ServiceRulesViolations,
} from '@lp/features/Scheduler/types';
import { baseRowClassname } from '@lp/features/Scheduler/constants';
import { getIsHighlightedCell, getScheduleDayViolations } from '@lp/features/Scheduler/utils';
import { DayColumnHeader, UserProfileCell, UserProfileHeader } from '@lp/features/Scheduler/components';
import { useDayColumns, useSchedulerTableAutoScroll, useSchedulerUserRows } from '@lp/features/Scheduler/hooks';
import { CS_SHIFT_TYPE_SELECT_LIST } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/constants';
import { CSShiftOptionType } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/types';
import { getCSShiftSetData, isCSExternalShift } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/utils';
import {
  isCSDeductionRulesMode,
  isSameCSWindow,
} from '@lp/features/Scheduler/SchedulerClinicalSpecialists/period.utils';
import { useCSSchedulerFiltersStore, useCSSchedulerHighlightStore } from '@lp/core/store/scheduler';
import {
  Hub,
  CSResource,
  CSResourcesWithShifts,
  SetCSShiftData,
  CSShiftSelectPayload,
} from '@/api/apis/resources/schedule';
import { shiftTimeGroupNames } from '@/constants/schedule.constants';
import {
  CSDayCell,
  CSOnCallTotalsSubTable,
  CSReservationCell,
  CSResourceShiftTotalsTable,
  CSUserCellSubtitle,
} from './components';
import { useCSShiftSiblingMaps } from './hooks';

type CSSchedulerTableProps = {
  hub: Hub;
  isEditable: boolean;
  isReservationsView: boolean;
  canResetShifts: boolean;
  canUnblockShifts?: boolean;
  selectedDate: Date;
  daysInterval: Date[];
  userList: CSResource[];
  hubsDictionary: HubsDataDictionary;
  shiftsData: CSResourcesWithShifts;
  onShiftChange: (resourceId: string, day: string, payload?: SetCSShiftData) => void;
  onUnblockShift?: (resourceId: string, day: string, scheduleId: string) => void;
  withHubTotals?: boolean;
  shiftTotalsMap?: ResourceShiftTotalsMap<CSShiftOptionType>;
  reservationsMap?: ResourceReservationsToDayMapsType;
  hubViolations?: ServiceRulesViolations;
  resourceViolations?: Record<string, ResourceViolationItem>;
};

export const CSSchedulerTable = ({
  hub,
  isEditable,
  isReservationsView,
  canResetShifts,
  canUnblockShifts,
  selectedDate,
  daysInterval,
  userList,
  hubsDictionary,
  hubViolations,
  resourceViolations,
  shiftsData,
  shiftTotalsMap,
  reservationsMap,
  onShiftChange,
  onUnblockShift,
  withHubTotals,
}: CSSchedulerTableProps) => {
  const filters = useCSSchedulerFiltersStore();
  const highlightValue = useCSSchedulerHighlightStore.use.value();
  const { scrollContainerRef, rowRefCallback, columnRefCallback, scrollToCell } =
    useSchedulerTableAutoScroll(selectedDate);
  const dayCols = useDayColumns(daysInterval, selectedDate, isSameCSWindow);
  const windowUsesDeductionRules = isCSDeductionRulesMode(selectedDate);
  const siblingMaps = useCSShiftSiblingMaps(dayCols, userList, shiftsData, hub.id, hubsDictionary);
  const { userRows, sortAsc, toggleSortAsc, daysFilters, setDayFilter } = useSchedulerUserRows({
    dayCols,
    userList,
    shiftsData,
    filters,
  });

  useEffect(() => {
    if (highlightValue?.hubId === hub.id) {
      const resourceId = highlightValue.resourceId ?? userRows[0]?.id;
      const day = highlightValue.day ?? (dayCols.find((col) => col.isCurrentMonth)?.date as Date);

      requestAnimationFrame(() => {
        setTimeout(() => scrollToCell(resourceId, day, true), 150);
      });
    }
  }, [dayCols, highlightValue, hub.id, scrollToCell, userRows]);

  // TODO: Make working callbacks memoization
  const getShiftChangeHandler = useCallback(
    (resourceId: string, day: string) => (shiftPayload?: CSShiftSelectPayload) => {
      if (!shiftPayload) {
        onShiftChange(resourceId, day);
        return;
      }

      const changeData = getCSShiftSetData(shiftPayload, shiftsData[resourceId]?.resource, hub.id, hub.regionId);

      onShiftChange(resourceId, day, changeData);
    },
    [hub, shiftsData, onShiftChange],
  );

  const getUnblockHandler = useCallback(
    (resourceId: string, day: string) => () => {
      const shift = shiftsData[resourceId]?.days[day];
      if (shift && onUnblockShift) {
        onUnblockShift(resourceId, day, shift.scheduleId);
      }
    },
    [shiftsData, onUnblockShift],
  );

  const renderShiftCell = useCallback(
    (column: SchedulerDayColumn, resourceId: string) => {
      const day = column.key;
      const { resource } = shiftsData[resourceId];
      const shift = shiftsData[resourceId].days[day];
      const siblingData = siblingMaps[resourceId].get(day);
      const handler = getShiftChangeHandler(resourceId, day);
      const highlight =
        highlightValue?.hubId === hub.id && getIsHighlightedCell(resourceId, column.date, highlightValue);

      // Previous-day column — needed to paint gap/overlap edge patches (color + per-period opacity).
      const colIndex = dayCols.findIndex((c) => c.key === day);
      const prevCol = colIndex > 0 ? dayCols[colIndex - 1] : undefined;
      const prevShift = prevCol ? shiftsData[resourceId].days[prevCol.key] : undefined;
      const prevLighten = prevCol ? !prevCol.isCurrentMonth : false;
      const prevIsExternal = isCSExternalShift(hub.id, prevShift);

      return (
        <CSDayCell
          key={column.key}
          column={column}
          resource={resource}
          shiftData={shift}
          prevShift={prevShift}
          prevLighten={prevLighten}
          prevIsExternal={prevIsExternal}
          siblingData={siblingData}
          isEditable={isEditable}
          canReset={canResetShifts}
          highlight={highlight}
          canUnblock={canUnblockShifts}
          usesDeductionRules={isCSDeductionRulesMode(column.date)}
          onShiftChange={handler}
          onUnblock={canUnblockShifts ? getUnblockHandler(resourceId, day) : undefined}
        />
      );
    },
    [
      shiftsData,
      siblingMaps,
      dayCols,
      getShiftChangeHandler,
      getUnblockHandler,
      highlightValue,
      hub.id,
      isEditable,
      canResetShifts,
      canUnblockShifts,
    ],
  );

  const renderReservationCell = useCallback(
    (column: SchedulerDayColumn, resourceId: string) => {
      const day = column.key;
      const shift = shiftsData[resourceId].days[day];
      const reservations = reservationsMap?.[resourceId]?.[day];

      return <CSReservationCell key={column.key} column={column} shiftData={shift} reservations={reservations} />;
    },
    [shiftsData, reservationsMap],
  );

  const renderDayCell = useMemo(() => {
    return isReservationsView ? renderReservationCell : renderShiftCell;
  }, [isReservationsView, renderReservationCell, renderShiftCell]);

  return (
    <div className="relative">
      <div ref={scrollContainerRef} className="w-full overflow-auto">
        <table aria-label="Schedule table" className="relative border-separate border-spacing-0 pb-4 pr-4">
          <thead>
            <tr className="h-10 text-left [&>th]:px-3 [&>th]:text-s-sb first:[&>th]:rounded-s-lg last:[&>th]:rounded-e-lg">
              <UserProfileHeader sortAsc={sortAsc} toggleSortAsc={toggleSortAsc} />
              {dayCols.map((column) => (
                <DayColumnHeader
                  ref={(el) => columnRefCallback(el, column.date)}
                  key={column.key}
                  column={column}
                  withFilter={filters.statusFilters}
                  filterOptionList={CS_SHIFT_TYPE_SELECT_LIST}
                  violations={getScheduleDayViolations(column.key, hubViolations)}
                  selectedKeys={daysFilters[column.key]}
                  onSelectedKeysChange={(shiftTypes) => setDayFilter(column.key, shiftTypes)}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {userRows.map((user) => (
              <tr key={user.id} ref={(el) => rowRefCallback(user.id, el)} className={baseRowClassname}>
                <UserProfileCell
                  key="user"
                  user={user}
                  violation={resourceViolations?.[user.id]}
                  titleSuffix={
                    // A window-level decision, not a per-day one: the row spans the whole window.
                    windowUsesDeductionRules && user.shiftTimeGroup
                      ? `· ${shiftTimeGroupNames[user.shiftTimeGroup]}`
                      : undefined
                  }
                >
                  <CSUserCellSubtitle user={user} />
                </UserProfileCell>
                {dayCols.map((column) => renderDayCell(column, user.id))}
              </tr>
            ))}
            {withHubTotals && (
              <CSOnCallTotalsSubTable hubIds={[hub.id]} dayCols={dayCols} userList={userRows} shiftsData={shiftsData} />
            )}
          </tbody>
        </table>
      </div>
      {filters.shiftTotals && !withHubTotals && (
        <CSResourceShiftTotalsTable userRows={userRows} shiftTotalsMap={shiftTotalsMap} />
      )}
    </div>
  );
};
